// packages import
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
// component import
import { AllCases } from '../components/cases/allCases'
import { CaseActionType } from '../components/cases/caseReducer'
import { MyCases } from '../components/cases/myCases'
import { SearchMatterContent } from '../components/cases/SearchMatterContent'
// others
import { useLocation, useSearchParams } from 'react-router-dom'
import { BillingType, CaseStatus, OrderCaseBy, useFetchAllCasesLazyQuery } from '../../generated'
import { TabPanel } from '../components/common/TabPanel'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS, SORT_DIR_TYPE } from '../constants'
import { AppContext, AuthContext, CaseContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import { getUserRole, hasAdminAccess } from '../utils'
import { CardBox, CustomTabButton } from '../theme/styleComponents'

export const CasesContainer: FC = (): JSX.Element => {
  const { dispatch: reducerDispatch, currentPage } = useContext(AppContext)
  const { currentUser, isParalegal, isAttorney, isInvestigator, isStaff, isDcpp } = useContext(AuthContext)
  const {
    lawTypeId,
    dispatch,
    caseFilter,
    searchValue,
    sortCasesByUser,
    orderBy,
    orderByDirection,
    clientId,
    officeName,
    billingType,
    isWorkCompleted
  } = useContext(CaseContext)
  const { id, roles } = currentUser || {}
  const [searchParams, setSearchParams] = useSearchParams()

  const { pathname } = useLocation()
  const isConsultPath = pathname.includes('consult')
  const hasDataLoadedRef = useRef(false)

  // Top-level tab state: 0 = Search Matters, 1 = Active, 2 = Work Completed, 3 = Closed
  // On consult path, hide Search Matters tab and default to Active (index 1)
  // On client-matters path, default to Search Matters (index 0)
  const [topLevelTab, setTopLevelTab] = useState<number>(() => {
    const activeTab = searchParams.get('activeTab')
    if (isConsultPath) {
      // On consult path, default to Active (index 1), ignore Search Matters (index 0)
      const tabValue = activeTab ? parseInt(activeTab) : 1
      return tabValue === 0 ? 1 : tabValue // If somehow 0, default to 1
    }
    // On client-matters path, default to Search Matters (index 0) if no activeTab
    return activeTab ? parseInt(activeTab) : 0
  })

  // Update tab when pathname changes (e.g., switching between consult and client-matters)
  useEffect(() => {
    if (isConsultPath && topLevelTab === 0) {
      // If on consult path and Search Matters is selected, switch to Active
      setTopLevelTab(1)
      searchParams.set('activeTab', '1')
      setSearchParams(searchParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsultPath])

  // Custom fetch policy for Cases page - use cache-first to avoid refetching on navigation
  const casesFetchPolicy = useMemo(
    () => ({
      fetchPolicy: 'cache-first' as const,
      nextFetchPolicy: 'cache-first' as const,
      notifyOnNetworkStatusChange: true
    }),
    []
  )

  const [fetchAllCases, { loading, refetch }] = useFetchAllCasesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      dispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllCases } = data

        if (fetchAllCases) {
          const { cases, pagination } = fetchAllCases

          if (cases) {
            dispatch({ type: CaseActionType.SET_ALL_CASES, allCases: cases || [] })
            hasDataLoadedRef.current = true
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  // Memoize query variables to prevent unnecessary refetches
  // Use debouncedSearchValue instead of searchValue to avoid API calls on every keystroke
  const queryVariables = useMemo(
    () => ({
      casesInput: {
        paginationOptions: {
          page: currentPage,
          limit: 10
        },
        orderBy,
        orderByDirection,
        isConsultCase: isConsultPath,
        status: caseFilter === 'close' ? CaseStatus.Completed : CaseStatus.Active,
        searchValue: searchValue || undefined,
        ...(sortCasesByUser?.id && { userId: sortCasesByUser?.id }),
        lawTypeId: lawTypeId,
        clientId: clientId,
        officeName,
        billingType: billingType === QUERY_PARAMS.ALL ? undefined : (billingType as BillingType),
        isWorkCompleted: caseFilter === 'close' ? undefined : isWorkCompleted
      }
    }),
    [
      currentPage,
      orderBy,
      orderByDirection,
      isConsultPath,
      caseFilter,
      searchValue,
      sortCasesByUser?.id,
      lawTypeId,
      clientId,
      officeName,
      billingType,
      isWorkCompleted
    ]
  )

  useEffect(() => {
    fetchAllCases({
      variables: queryVariables
    })
    // eslint-disable-next-line
  }, [fetchAllCases, queryVariables])

  useEffect(() => {
    dispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })

    if (id && (isParalegal || isAttorney)) {
      searchParams.set('userId', id)
      searchParams.set('userLabel', 'My Matters')
      searchParams.set('userRole', roles?.[0]?.role || '')
      setSearchParams(searchParams)
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // Fetching data from params and setting the global state accordingly to refetch data
    const activeTab = searchParams.get('activeTab')
    const lawType = searchParams.get('lawType')
    const caseFilter = searchParams.get('caseFilter')
    const currentPage = searchParams.get('currentPage')
    const searchValue = searchParams.get('searchValue')
    const userId = searchParams.get('userId')
    const userRolesParam = searchParams.get('userRole')
    const userLabel = searchParams.get('userLabel')
    const caseOrderBy = searchParams.get('orderBy')
    const caseOrderByDirection = searchParams.get(QUERY_PARAMS.ORDER_DIRECTION) as SORT_DIR_TYPE
    const billingType = searchParams.get(QUERY_PARAMS.BILLING_TYPE) as BillingType
    const isWorkCompleted = searchParams.get(QUERY_PARAMS.IS_WORK_COMPLETED)

    caseOrderByDirection &&
      dispatch({ type: CaseActionType.SET_CASE_ORDER_DIRECTION, orderByDirection: caseOrderByDirection })
    caseOrderBy && dispatch({ type: CaseActionType.SET_CASE_ORDER_BY, orderBy: caseOrderBy as OrderCaseBy })
    lawType && dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: lawType })
    searchValue && dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: searchValue })
    caseFilter && dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: searchParams.get('caseFilter')! })
    currentPage && reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: parseInt(currentPage) })
    // Handle top-level tab from URL
    if (activeTab) {
      const tabValue = parseInt(activeTab)
      // On consult path, if tab is 0 (Search Matters), default to 1 (Active)
      const finalTabValue = isConsultPath && tabValue === 0 ? 1 : tabValue
      setTopLevelTab(finalTabValue)
      // If tab is 1, 2, or 3, set the appropriate case filter
      if (finalTabValue === 1) {
        dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
        dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: false })
      } else if (finalTabValue === 2) {
        dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
        dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: true })
      } else if (finalTabValue === 3) {
        dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'close' })
        dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: undefined })
      }
    } else {
      // No activeTab in URL - set default based on path
      if (isConsultPath) {
        setTopLevelTab(1)
        searchParams.set('activeTab', '1')
      } else {
        // Client-matters path: default to Search Matters (index 0)
        setTopLevelTab(0)
        searchParams.set('activeTab', '0')
      }
      setSearchParams(searchParams)
    }
    billingType && dispatch({ type: CaseActionType.SET_BILLING_TYPE, billingType: billingType })
    isWorkCompleted &&
      dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: isWorkCompleted === 'yes' })
    if (userId && userRolesParam && userLabel) {
      dispatch({
        type: CaseActionType.SET_SORT_CASES_BY_USER,
        sortCasesByUser: { id: userId, role: getUserRole(userRolesParam), label: userLabel }
      })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
      dispatch({ type: CaseActionType.SET_BILLING_TYPE, billingType: undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleTopLevelTabChange = (tabIndex: number) => {
    setTopLevelTab(tabIndex)
    searchParams.set('activeTab', tabIndex.toString())

    // Reset filters when switching tabs
    if (tabIndex === 0) {
      // Search Matters tab - no filter changes needed
    } else if (tabIndex === 1) {
      // Active tab
      dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
      dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: false })
      searchParams.set('caseFilter', 'active')
      searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'no')
    } else if (tabIndex === 2) {
      // Work Completed tab
      dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
      dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: true })
      searchParams.set('caseFilter', 'active')
      searchParams.set(QUERY_PARAMS.IS_WORK_COMPLETED, 'yes')
    } else if (tabIndex === 3) {
      // Closed tab
      dispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'close' })
      dispatch({ type: CaseActionType.SET_IS_WORK_COMPLETED, isWorkCompleted: undefined })
      searchParams.set('caseFilter', 'close')
      searchParams.delete(QUERY_PARAMS.IS_WORK_COMPLETED)
    }

    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set('currentPage', '1')
    setSearchParams(searchParams)
  }

  return (
    <CardBox marginTop={3}>
      <Box padding="20px 0px 0px 15px">
        {!isConsultPath && (
          <CustomTabButton
            isLeftItem
            onClick={() => handleTopLevelTabChange(0)}
            className={topLevelTab === 0 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
          >
            Search Matters
          </CustomTabButton>
        )}
        <CustomTabButton
          isLeftItem={isConsultPath}
          isCenterItem={!isConsultPath}
          onClick={() => handleTopLevelTabChange(1)}
          className={topLevelTab === 1 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Active
        </CustomTabButton>
        <CustomTabButton
          isCenterItem
          onClick={() => handleTopLevelTabChange(2)}
          className={topLevelTab === 2 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Work Completed
        </CustomTabButton>
        <CustomTabButton
          onClick={() => handleTopLevelTabChange(3)}
          className={topLevelTab === 3 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
        >
          Closed
        </CustomTabButton>
      </Box>

      {!isConsultPath && (
        <TabPanel value={topLevelTab} index={0}>
          <SearchMatterContent />
        </TabPanel>
      )}

      {(hasAdminAccess(currentUser) || isParalegal || isAttorney || isStaff || isDcpp) && (
        <>
          <TabPanel value={topLevelTab} index={1}>
            <AllCases
              loading={loading}
              refetch={refetch}
              isConsultPath={isConsultPath}
              key={pathname}
              hideTabs
              fetchAllCases={fetchAllCases}
            />
          </TabPanel>
          <TabPanel value={topLevelTab} index={2}>
            <AllCases
              loading={loading}
              refetch={refetch}
              isConsultPath={isConsultPath}
              key={pathname}
              hideTabs
              fetchAllCases={fetchAllCases}
            />
          </TabPanel>
          <TabPanel value={topLevelTab} index={3}>
            <AllCases
              loading={loading}
              refetch={refetch}
              isConsultPath={isConsultPath}
              key={pathname}
              hideTabs
              fetchAllCases={fetchAllCases}
            />
          </TabPanel>
        </>
      )}

      {isInvestigator && (
        <>
          <TabPanel value={topLevelTab} index={1}>
            <MyCases loading={loading} refetch={refetch} isConsultPath={isConsultPath} key={pathname} />
          </TabPanel>
          <TabPanel value={topLevelTab} index={2}>
            <MyCases loading={loading} refetch={refetch} isConsultPath={isConsultPath} key={pathname} />
          </TabPanel>
          <TabPanel value={topLevelTab} index={3}>
            <MyCases loading={loading} refetch={refetch} isConsultPath={isConsultPath} key={pathname} />
          </TabPanel>
        </>
      )}
    </CardBox>
  )
}
